import React, { useState } from "react";
import { RiCloseFill } from 'react-icons/ri';
import { post } from "../../utility/fetchAdmin";
import notification from "../../utility/notification";
import * as XLSX from 'xlsx';

function BulkLabServicesUploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [isDryRunSuccessful, setIsDryRunSuccessful] = useState(false);

  const userId = sessionStorage.getItem("userId");

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError("");
    setMessage("");
    setPreviewData(null);
    setDryRunResult(null);
    setIsDryRunSuccessful(false);
  };

  const endpoint = "/templates/bulk-lab-services-upload";

  const runUpload = async (dryRunValue) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("dryRun", dryRunValue);

    return post(endpoint, formData);
  };

  const performDryRunAndPreview = async () => {
    setError("");
    setMessage("");
    setPreviewData(null);
    setDryRunResult(null);

    if (!file) {
      const msg = "Please select an Excel file first.";
      setError(msg);
      notification({ message: msg, type: "error" });
      return;
    }

    if (!userId) {
      const msg = "User ID is missing from sessionStorage.";
      setError(msg);
      notification({ message: msg, type: "error" });
      return;
    }

    setLoading(true);

    try {
      const dryRunResponse = await runUpload(true);

      if (!dryRunResponse) {
        const errMsg = "Dry run failed. No response from server.";
        setError(errMsg);
        notification({ message: errMsg, type: "error" });
        return;
      }

      if (dryRunResponse.mode !== "DRY-RUN" || dryRunResponse.message !== "Validation successful. Safe to upload.") {
        const errMsg = dryRunResponse.message || "Dry run failed.";
        setError(errMsg);
        notification({ message: errMsg, type: "error" });
        return;
      }

      setDryRunResult(dryRunResponse);
      setIsDryRunSuccessful(true);
      notification({ message: "Dry run successful. Preview is ready.", type: "success" });

      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setPreviewData(jsonData);
      setMessage(`Dry run passed. Showing preview from sheet: ${firstSheetName}.`);
    } catch (err) {
      const errMsg = err?.message || "An unexpected error occurred during dry run.";
      setError(errMsg);
      notification({ message: errMsg, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const confirmUpload = async () => {
    if (!isDryRunSuccessful) {
      const msg = "Run dry-run first to view preview before uploading.";
      setError(msg);
      notification({ message: msg, type: 'warning' });
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const finalResponse = await runUpload(false);

      if (!finalResponse || !finalResponse?.successfullyCreated) {
        const errMsg = finalResponse?.message || "Upload failed.";
        setError(errMsg);
        notification({ message: errMsg, type: "error" });
        return;
      }

      const successMessage = finalResponse?.message || "Bulk lab services uploaded successfully.";
      setMessage(successMessage);
      notification({ message: successMessage, type: "success" });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const errMsg = err?.message || "An unexpected error occurred during upload.";
      setError(errMsg);
      notification({ message: errMsg, type: "error" });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="overlay" style={{ padding: 40 }}>
      <div className="modal-content w-40" style={{ maxWidth: '560px', width: '100%', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: '26px', position: 'relative' }}>
        <RiCloseFill className='close-btn pointer' onClick={onClose} style={{position: 'absolute', top: 16, right: 16, color: '#333', fontSize: '1.8rem', backgroundColor: '#f7f7f7', borderRadius: '50%', padding: '6px'}} />

        <h2 style={{ marginBottom: 12 }}>Bulk Lab Services Upload</h2>
        <p style={{ color: '#5a5a5a', marginBottom: 18 }}>Upload an Excel file to add lab services in bulk.</p>

        <div style={{ marginBottom: 18 }}>
          <label htmlFor="bulk-lab-file" className="bolder" style={{ display: 'block', marginBottom: 8 }}>Choose Excel file</label>
          <input
            id="bulk-lab-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #d2d2d2' }}
          />
        </div>

        {error && <p style={{ color: '#b00020', background: '#ffe4ea', borderRadius: '8px', padding: '10px', marginBottom: 12 }}>{error}</p>}
        {message && <p style={{ color: '#1f7a24', background: '#e2f7df', borderRadius: '8px', padding: '10px', marginBottom: 12 }}>{message}</p>}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            disabled={loading}
            onClick={performDryRunAndPreview}
            className="submit-btn"
            style={{ flex: '1 1 auto', minWidth: 110, backgroundColor: '#0a7d3b', borderColor: '#0a7d3b', color: '#fff', padding: '11px 16px', borderRadius: '8px' }}
          >
            {loading ? "Processing..." : "Dry Run & Preview"}
          </button>
          <button
            disabled={loading || !isDryRunSuccessful}
            onClick={confirmUpload}
            className="submit-btn"
            style={{ flex: '1 1 auto', minWidth: 110, backgroundColor: isDryRunSuccessful ? '#0a4b26' : '#b1b1b1', borderColor: isDryRunSuccessful ? '#0a4b26' : '#b1b1b1', color: '#fff', padding: '11px 16px', borderRadius: '8px' }}
          >
            {loading ? "Uploading..." : "Confirm Upload"}
          </button>
          <button
            onClick={onClose}
            className="btn"
            style={{ flex: '1 1 auto', minWidth: 110, backgroundColor: '#e7e7e7', color: '#333', borderRadius: '8px', border: '1px solid #ddd', padding: '11px 16px' }}
          >
            Cancel
          </button>
        </div>

        {previewData && previewData.length > 0 && (
          <div style={{ marginTop: 20, border: '1px solid #ddd', borderRadius: 10, maxHeight: '250px', overflow: 'auto', padding: 12, backgroundColor: '#fafafa' }}>
            <p style={{ margin: '0 0 10px', fontWeight: 'bold', color: '#333' }}>Preview (first 20 rows)</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {previewData[0].map((heading, idx) => (
                    <th key={idx} style={{ padding: '6px 8px', borderBottom: '1px solid #ccc', textAlign: 'left', backgroundColor: '#f2f2f2' }}>{heading || `Column ${idx + 1}`}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.slice(1, 21).map((row, rowIndex) => (
                  <tr key={rowIndex} style={{ borderBottom: '1px solid #eee' }}>
                    {previewData[0].map((_, colIndex) => (
                      <td key={colIndex} style={{ padding: '6px 8px', borderRight: '1px solid #f1f1f1' }}>{row[colIndex] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.length > 21 && <div style={{ marginTop: 8, color: '#666' }}>Only first 20 rows shown.</div>}
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 13, color: '#666' }}>
          <p><strong>Note:</strong> Excel must contain valid fields; invalid rows will be skipped.</p>
        </div>
      </div>
    </div>
  );
}

export default BulkLabServicesUploadModal;
